import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type {
    AttemptQuestionDetailsDTO,
    NavigationQuestionDTO,
} from '../models/StudentExamViewDTOs';
import { ErrorMessage, SimpleButton } from '@/src/ui';
import { translate } from '@/src/utils';
import { apiRequest } from '@/src/services';

type FinishSegmentButtonProps = {
    loading: boolean;
    setError: (err: string) => void;
    language: 'en' | 'es';
    questionDetails: AttemptQuestionDetailsDTO;
    navData?: NavigationQuestionDTO[];
};

export default function FinishSegmentButton({
    loading,
    setError,
    language,
    questionDetails,
    navData,
}: FinishSegmentButtonProps) {
    const navigate = useNavigate();
    const { attemptId = '0' } = useParams();

    const nextQuestionId = (() => {
        if (!navData) {
            return null;
        }
        const currentId = questionDetails.id;
        const currentIndex = navData.findIndex((q) => q.id === currentId);
        return navData[currentIndex + 1]?.id || null;
    })();

    const areAllQuestionsAnswered = (() => {
        if (!navData) {
            return false;
        }
        const currentId = questionDetails.id;
        const currentSegmentNumber = navData.find((q) => q.id === currentId)?.segmentNumber;
        if (!currentSegmentNumber) {
            return false;
        }

        const segmentQuestions = navData.filter((q) => q.segmentNumber === currentSegmentNumber);
        return segmentQuestions.every((q) => q.isAnswered || q.id === currentId);
    })();

    const submitSegment = useCallback(async () => {
        try {
            if (!navData) {
                return;
            }
            setError('');

            await apiRequest('post', `assessments/studentExamView/submitSegment`, {
                attemptId,
                attemptQuestionId: questionDetails.id,
            });

            navigate(`/studentExamView/attempt/${attemptId}/q/${nextQuestionId}`, {
                replace: true,
            });
        } catch {
            setError('Failed to submit segment');
        }
    }, [attemptId, questionDetails, nextQuestionId, navigate, setError, navData]);

    return (
        <div className="assessment-nav-pane flex flex-row p-4 gap-x-4">
            <div>
                {!loading && !areAllQuestionsAnswered && (
                    <ErrorMessage error={translate(language, 'please_answer_all')} />
                )}
            </div>
            <div className="ml-auto">
                <SimpleButton
                    color="info"
                    onClick={submitSegment}
                    disabled={loading || !areAllQuestionsAnswered}
                >
                    {translate(language, 'finish_segment')}
                </SimpleButton>
            </div>
        </div>
    );
}
